import { useEffect, useRef } from 'react';

import { PrimaryProductKeys } from '../../../common/constants/primary-product-keys';
import { usePrimaryProduct } from '../../../controllers/store/bootstrap';
import { preloadSearchDialog } from './async';

/**
 * Decides *when* the dialog chunk is fetched, never whether it is split — `SearchDialogAsync` is
 * used unconditionally, since a static import would keep the dialog in the eager bundle for
 * everyone and nobody would save anything.
 *
 * - every product but Confluence: fetch on mount.
 * - Confluence: wait for hover or focus on the input, which strongly predicts opening. The keyboard
 *   shortcut is the one path that pays the fetch at open time.
 */
export function useSearchDialogPreload(isInteracted: boolean): void {
	const [primaryProduct] = usePrimaryProduct();

	// Latched on the first render where the product is known, and nothing preloads before then.
	// `primaryProduct` starts undefined, so reading it eagerly would latch "not Confluence" and
	// preload on mount for Confluence too, defeating the deferral.
	const shouldDeferRef = useRef<boolean | undefined>(undefined);
	if (shouldDeferRef.current === undefined && primaryProduct !== undefined) {
		shouldDeferRef.current = primaryProduct === PrimaryProductKeys.Confluence;
	}
	const shouldDefer = shouldDeferRef.current;

	useEffect(() => {
		if (shouldDefer === undefined) {
			return;
		}

		if (!shouldDefer || isInteracted) {
			preloadSearchDialog();
		}
	}, [shouldDefer, isInteracted]);
}
